import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Link, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import { fieldLabelRowSx } from '../common/formFieldStyles';
import { DepositType, PrepaymentMode, normalizePrepaymentMode } from '../../constants/PrepaymentMode';
import { calculateServicePrepaymentPreview } from '../../utils/prepaymentUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const inputSx = (hasError) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  minHeight: 48,
  '&:focus': { borderColor: BRAND.accent },
});

const MODE_HELP_KEYS = {
  [PrepaymentMode.NotRequired]: 'servicesManagement.prepayment.modeNotRequiredHelp',
  [PrepaymentMode.Deposit]: 'servicesManagement.prepayment.modeDepositHelp',
  [PrepaymentMode.FullPrepayment]: 'servicesManagement.prepayment.modeFullHelp',
};

/**
 * Lets the owner ask for a deposit or full prepayment on a service. The preview shows exactly
 * the split the customer will see, so the owner never has to do the maths.
 */
const ServicePrepaymentFields = ({
  value,
  price,
  currency,
  error,
  onlinePaymentsReady,
  storeId,
  onChange,
}) => {
  const { t } = useTranslation();
  const mode = normalizePrepaymentMode(value.prepaymentMode);
  const hasPrepayment = mode !== PrepaymentMode.NotRequired;
  // A service that already asks for a deposit can always be switched off, even when online
  // payments have since been disabled.
  const locked = !onlinePaymentsReady && !hasPrepayment;
  const isPercentage = value.depositType === DepositType.Percentage;

  const preview = useMemo(
    () => calculateServicePrepaymentPreview({ price, ...value }),
    [price, value],
  );

  const update = (changes) => onChange({ ...value, ...changes });

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        border: '1px solid',
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('servicesManagement.prepayment.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('servicesManagement.prepayment.help')}
        </Typography>
      </Box>

      {!onlinePaymentsReady ? (
        <Alert severity={hasPrepayment ? 'warning' : 'info'}>
          {t(hasPrepayment ? 'servicesManagement.prepayment.onlinePaymentsOffWarning' : 'servicesManagement.prepayment.onlinePaymentsRequired')}{' '}
          {storeId ? (
            <Link
              component={RouterLink}
              to={`/management/stores/${storeId}/payment-settings?tab=customer-payments`}
              sx={{ color: BRAND.accentText, fontWeight: 600 }}
            >
              {t('servicesManagement.prepayment.openPaymentSettings')}
            </Link>
          ) : null}
        </Alert>
      ) : null}

      <SalovareSelectField
        label={t('servicesManagement.prepayment.mode')}
        value={mode}
        disabled={locked}
        onChange={(event) => update({ prepaymentMode: event.target.value })}
      >
        <MenuItem value={PrepaymentMode.NotRequired}>{t('servicesManagement.prepayment.modeNotRequired')}</MenuItem>
        <MenuItem value={PrepaymentMode.Deposit} disabled={!onlinePaymentsReady}>
          {t('servicesManagement.prepayment.modeDeposit')}
        </MenuItem>
        <MenuItem value={PrepaymentMode.FullPrepayment} disabled={!onlinePaymentsReady}>
          {t('servicesManagement.prepayment.modeFull')}
        </MenuItem>
      </SalovareSelectField>
      <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
        {t(MODE_HELP_KEYS[mode])}
      </Typography>

      {hasPrepayment ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <SalovareSelectField
            label={t('servicesManagement.prepayment.depositType')}
            value={value.depositType || DepositType.FixedAmount}
            onChange={(event) => update({ depositType: event.target.value })}
          >
            <MenuItem value={DepositType.FixedAmount}>
              {t('servicesManagement.prepayment.depositTypeFixed', { currency })}
            </MenuItem>
            <MenuItem value={DepositType.Percentage}>{t('servicesManagement.prepayment.depositTypePercentage')}</MenuItem>
          </SalovareSelectField>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelRowSx}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {`${t(mode === PrepaymentMode.Deposit
                  ? 'servicesManagement.prepayment.depositValue'
                  : 'servicesManagement.prepayment.nonRefundableValue')} (${isPercentage ? '%' : currency})${mode === PrepaymentMode.Deposit ? ' *' : ''}`}
              </Typography>
            </Box>
            <Box
              component="input"
              type="number"
              aria-label={t(mode === PrepaymentMode.Deposit
                ? 'servicesManagement.prepayment.depositValue'
                : 'servicesManagement.prepayment.nonRefundableValue')}
              value={value.depositValue ?? ''}
              min={0}
              max={isPercentage ? 100 : undefined}
              step={isPercentage ? 1 : 0.01}
              onChange={(event) => update({ depositValue: event.target.value })}
              sx={inputSx(Boolean(error))}
            />
          </Box>
        </Box>
      ) : null}

      {error ? (
        <Typography variant="caption" sx={{ color: '#f44336', mt: -1 }}>
          {t(error)}
        </Typography>
      ) : null}

      {hasPrepayment ? (
        <Box sx={{ borderRadius: 2, p: 1.5, backgroundColor: brandAlpha('accent', 0.06), border: `1px solid ${brandAlpha('accent', 0.25)}` }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: BRAND.accentText }}>
            {t('servicesManagement.prepayment.previewTitle')}
          </Typography>
          {preview ? (
            <>
              <Typography variant="body2">
                {preview.payAtStore > 0
                  ? t('servicesManagement.prepayment.previewSplit', {
                    now: formatCurrency(preview.payNow, currency),
                    later: formatCurrency(preview.payAtStore, currency),
                  })
                  : t('servicesManagement.prepayment.previewFull', { now: formatCurrency(preview.payNow, currency) })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {preview.nonRefundable > 0
                  ? t('servicesManagement.prepayment.previewNonRefundable', { amount: formatCurrency(preview.nonRefundable, currency) })
                  : t('servicesManagement.prepayment.previewFeeOnly')}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('servicesManagement.prepayment.previewNeedsPrice')}
            </Typography>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default ServicePrepaymentFields;
